import API_BASE from "./apiConfig";
import React, { useState, useEffect, useRef } from "react";
import {
  FaEnvelope, FaLock, FaSignInAlt, FaExclamationCircle,
  FaChild, FaUserShield, FaChartLine, FaTrophy, FaCommentDots,
  FaUsers, FaCalendarAlt, FaCog, FaMoon, FaSun
} from "react-icons/fa";
import { useAppConfig } from "./AppContext";
import { useTheme, getT, glassCard } from "./ThemeContext";
import SixersLogo from "./Sixers_Logo.png";
import bg6 from "./Background images/6.jpg";
import bg8 from "./Background images/8.jpg";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=Exo+2:wght@300;400;500;600;700;800&display=swap');

  @keyframes lp_fadeUp  { from{opacity:0;transform:translateY(26px)} to{opacity:1;transform:translateY(0)} }
  @keyframes lp_fadeIn  { from{opacity:0} to{opacity:1} }
  @keyframes lp_scan    { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes lp_cardGlow {
    0%,100%{box-shadow:0 32px 80px rgba(0,0,0,0.15),0 0 22px rgba(247,206,11,0.05)}
    50%    {box-shadow:0 32px 80px rgba(0,0,0,0.25),0 0 44px rgba(247,206,11,0.15)}
  }
  @keyframes lp_spin      { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes lp_hex       { 0%,100%{transform:translateY(0) rotate(0deg);opacity:0.12} 50%{transform:translateY(-16px) rotate(5deg);opacity:0.24} }
  @keyframes lp_logoPulse { 0%,100%{filter:drop-shadow(0 0 15px rgba(247,206,11,0.3))} 50%{filter:drop-shadow(0 0 30px rgba(247,206,11,0.5))} }
  @keyframes lp_haloBlue   { 0%,100%{box-shadow:0 0 0 4px rgba(3,78,123,0.08),0 0 16px 4px rgba(3,78,123,0.18)} 50%{box-shadow:0 0 0 8px rgba(3,78,123,0.13),0 0 32px 10px rgba(3,78,123,0.36)} }
  @keyframes lp_haloYellow { 0%,100%{box-shadow:0 0 0 4px rgba(247,206,11,0.12),0 0 16px 4px rgba(247,206,11,0.28)} 50%{box-shadow:0 0 0 8px rgba(247,206,11,0.20),0 0 32px 10px rgba(247,206,11,0.55)} }

  .lp_input::placeholder{color:var(--text-muted);}
  .lp_tab {transition:all 0.24s cubic-bezier(.4,0,.2,1);}
  .lp_tab:hover{filter:brightness(1.10);}
  .lp_btn {transition:all 0.2s cubic-bezier(.4,0,.2,1);}
  .lp_btn:hover:not(:disabled){transform:translateY(-2px);filter:brightness(1.08);box-shadow: 0 8px 25px rgba(247,206,11,0.4);}
  .lp_btn:active:not(:disabled){transform:translateY(0);}
  .lp_feat{transition:all 0.2s; cursor: default;}
  .lp_wrap{transition:border-color 0.2s, background 0.2s;}
`;

const HEXES=[
  {x:5,y:7,s:1.3,d:0},{x:18,y:50,s:0.8,d:1.1},{x:62,y:10,s:1.1,d:0.6},{x:78,y:62,s:1.5,d:0.4},
  {x:42,y:78,s:0.7,d:1.7},{x:11,y:76,s:1.0,d:0.9},{x:86,y:27,s:0.9,d:1.4},{x:50,y:44,s:0.6,d:2.0},
];

function HexGrid({ color }) {
  return (
    <div style={{position:"absolute",inset:0,pointerEvents:"none",overflow:"hidden"}}>
      {HEXES.map((h,i)=>(
        <div key={i} style={{position:"absolute",left:`${h.x}%`,top:`${h.y}%`,width:64*h.s,height:64*h.s,animation:`lp_hex ${4+h.d}s ease-in-out ${h.d}s infinite`}}>
          <svg viewBox="0 0 100 100" style={{width:"100%",height:"100%"}}>
            <polygon points="50,2 93,26 93,74 50,98 7,74 7,26" fill="none" stroke={color} strokeWidth="1.2"/>
          </svg>
        </div>
      ))}
    </div>
  );
}

function Corners({color="#f7ce0b",size=24}) {
  const a={position:"absolute"}, b=(e={})=>({position:"absolute",background:color,...e}), T=2.5;
  return(<>
    <div style={{...a,top:-1,left:-1,width:size,height:size}}><div style={b({top:0,left:0,width:size,height:T})}/><div style={b({top:0,left:0,width:T,height:size})}/></div>
    <div style={{...a,top:-1,right:-1,width:size,height:size}}><div style={b({top:0,right:0,width:size,height:T})}/><div style={b({top:0,right:0,width:T,height:size})}/></div>
    <div style={{...a,bottom:-1,left:-1,width:size,height:size}}><div style={b({bottom:0,left:0,width:size,height:T})}/><div style={b({bottom:0,left:0,width:T,height:size})}/></div>
    <div style={{...a,bottom:-1,right:-1,width:size,height:size}}><div style={b({bottom:0,right:0,width:size,height:T})}/><div style={b({bottom:0,right:0,width:T,height:size})}/></div>
  </>);
}

export default function LoginPage({ onLogin }) {
  const { isDark, toggleTheme } = useTheme();
  const T = getT(isDark);
  const gc = glassCard(T);
  
  const { config } = useAppConfig();
  const secondary  = "#f7ce0b";
  const orgName    = config.org_name    || "Sixers Sports Center";
  const orgTagline = config.org_tagline || "Academy Intelligence";
  const logoSrc    = config.logo_url    || SixersLogo;

  const [portal,  setPortal]  = useState("staff");
  const [email,   setEmail]   = useState("");
  const [password,setPassword]= useState("");
  const [error,   setError]   = useState("");
  const [loading, setLoading] = useState(false);
  const [bgIdx,   setBgIdx]   = useState(0);
  const [mounted, setMounted] = useState(false);
  const isParent = portal==="parent";
  const googleBtnRef = useRef(null);

  useEffect(()=>{
    const t=setTimeout(()=>setMounted(true),80);
    const iv=setInterval(()=>setBgIdx(p=>(p+1)%2),8000);
    return()=>{clearTimeout(t);clearInterval(iv);};
  },[]);

  // Google Sign-In init
  useEffect(()=>{
    if(!isParent) return;
    const clientId=process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if(!clientId||clientId==="YOUR_GOOGLE_CLIENT_ID_HERE") return;

    const doRender=()=>{
      if(!window.google||!googleBtnRef.current) return;
      googleBtnRef.current.innerHTML="";
      try{
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async(credResp)=>{
            setError(""); setLoading(true);
            try{
              const res=await fetch(`${API_BASE}/parent-auth/google-login`,{
                method:"POST",
                headers:{"Content-Type":"application/json"},
                body:JSON.stringify({id_token:credResp.credential}),
              });
              const data=await res.json();
              if(data.success) onLogin(data.user);
              else setError(data.message||"Google login failed.");
            }catch{ setError("Server unreachable."); }
            finally{ setLoading(false); }
          },
        });
        window.google.accounts.id.renderButton(googleBtnRef.current,{
          theme: isDark ? "filled_black" : "filled_blue",
          size:"large",
          width: 400,
          text:"signin_with",
        });
      }catch(e){ console.warn(e); }
    };
    const t=setTimeout(doRender,100);
    return()=>clearTimeout(t);
  },[isParent, isDark, onLogin]);

  const switchPortal=p=>{setPortal(p);setError("");setEmail("");setPassword("");};

  const handleSubmit=async(e)=>{
    e.preventDefault(); setError("");
    if(!email||!password){setError("Credentials required.");return;}
    setLoading(true);
    try{
      const ep=isParent?`${API_BASE}/parent-auth/login`:`${API_BASE}/auth/login`;
      const res=await fetch(ep,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({email,password})});
      const data=await res.json();
      if(data.success) onLogin(data.user);
      else setError(data.message||"Invalid credentials.");
    }catch{setError("Server connection failed.");}
    finally{setLoading(false);}
  };

  const staffFeatures=[
    {icon:FaUsers,       text:"Full performance analytics",  tag:"CORE"},
    {icon:FaCalendarAlt, text:"Attendance & scheduling",     tag:"LIVE"},
    {icon:FaTrophy,      text:"KPI scoring engine",          tag:"STATS"},
    {icon:FaCog,         text:"Enterprise management",      tag:"SEC" },
  ];
  const parentFeatures=[
    {icon:FaChartLine,   text:"Live progress tracking",      tag:"LIVE"},
    {icon:FaTrophy,      text:"Technical category stats",   tag:"STATS"},
    {icon:FaCommentDots, text:"Coach session feedback",      tag:"FEED"},
    {icon:FaChild,       text:"Student profile control",    tag:"ACCT"},
  ];
  const features = isParent ? parentFeatures : staffFeatures;
  
  const F = "'Exo 2', sans-serif";
  const D = "'Rajdhani', sans-serif";

  return(
    <>
      <style>{CSS}</style>
      <div style={{
        minHeight:"100vh", fontFamily:F, overflow:"hidden", position:"relative", 
        background: T.pageBg, display:"flex", transition: "background 0.5s ease"
      }}>

        {/* Global CSS Variables for the theme */}
        <div style={{ display: 'none' }}>
           <style>{`
             :root {
               --text-muted: ${T.textMuted};
             }
           `}</style>
        </div>

        {/* Background Images with Theme-based Overlay */}
        {[bg6,bg8].map((src,i)=>(
          <div key={i} style={{position:"absolute",inset:0,zIndex:0,backgroundImage:`url(${src})`,backgroundSize:"cover",backgroundPosition:"center 25%",opacity:bgIdx===i ? (isDark ? 0.45 : 0.35) : 0, transition:"opacity 1.5s ease-in-out"}}/>
        ))}

        <div style={{
          position:"absolute",inset:0,zIndex:1,
          background: isDark 
            ? "linear-gradient(135deg, rgba(2,13,24,0.95) 0%, rgba(2,13,24,0.85) 100%)"
            : "linear-gradient(135deg, rgba(240,246,255,0.55) 0%, rgba(255,255,255,0.45) 100%)"
        }}/>
        
        <div style={{position:"absolute",inset:0,zIndex:2}}><HexGrid color={isDark ? "rgba(247,206,11,0.22)" : "rgba(3,78,123,0.12)"}/></div>
        
        {/* Scanned Lines / Scan effect */}
        <div style={{position:"absolute",inset:0,zIndex:3,pointerEvents:"none",overflow:"hidden"}}>
          <div style={{position:"absolute",left:0,right:0,height:2,background: isDark ? "rgba(247,206,11,0.08)" : "rgba(3,78,123,0.04)", animation:"lp_scan 12s linear infinite"}}/>
          <div style={{position:"absolute",inset:0,backgroundImage: isDark ? "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(255,255,255,0.015) 2px,rgba(255,255,255,0.015) 4px)" : "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.01) 2px,rgba(0,0,0,0.01) 4px)"}}/>
        </div>

        {/* Theme Toggle Button */}
        <button onClick={toggleTheme} title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"} style={{
          position: "absolute", top: 24, left: 24, zIndex: 100,
          width: 36, height: 36, borderRadius: "10px", background: T.glass,
          border: T.glassBdr, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          color: T.yellow, boxShadow: T.glassSh, transition: "0.2s"
        }}>
          {isDark ? <FaSun size={15}/> : <FaMoon size={15}/>}
        </button>

        <div style={{position:"relative",zIndex:6,width:"100%",display:"flex",minHeight:"100vh",opacity:mounted?1:0,transition:"opacity 0.6s ease"}}>

          {/* LEFT: Branding & Features */}
          <div style={{flex:"0 0 50%",display:"flex",flexDirection:"column",justifyContent:"center",padding:"0 64px"}}>
            <div style={{display:"flex",alignItems:"center",gap:24,marginBottom:20}}>
              <div style={{position:"relative",flexShrink:0,width:134,height:134}}>
                {/* Halo ring — circular container with inner padding so logo never touches edge */}
                <div style={{
                  position:"absolute", inset:0, borderRadius:"50%",
                  border: isDark ? "2.5px solid rgba(247,206,11,0.65)" : "2.5px solid rgba(3,78,123,0.50)",
                  background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.88)",
                  animation: isDark ? "lp_haloYellow 2.6s ease-in-out infinite" : "lp_haloBlue 2.6s ease-in-out infinite",
                  display:"flex", alignItems:"center", justifyContent:"center",
                  zIndex:1,
                }}>
                  <img src={logoSrc} alt={orgName} style={{width:96,objectFit:"contain",animation:"lp_logoPulse 3s ease-in-out infinite"}}/>
                </div>
                {/* Spinning dashed outer ring */}
                <div style={{position:"absolute",inset:-20,border:`2px dashed ${isDark ? "rgba(247,206,11,0.2)" : "rgba(3,78,123,0.1)"}`,borderRadius:"50%",animation:"lp_spin 30s linear infinite"}}/>
              </div>
              <div>
                <h1 style={{fontFamily:D,fontSize:32,fontWeight:900,color:T.textPrimary,margin:0,letterSpacing:"0.06em"}}>
                  {orgName.toUpperCase()}
                </h1>
                <div style={{display:"flex",alignItems:"center",gap:9,marginTop:8}}>
                  <div style={{width:26,height:3,background:T.yellow,borderRadius:2}}/>
                  <span style={{fontFamily:F,fontSize:12,fontWeight:700,color:T.yellow,letterSpacing:"0.16em",textTransform:"uppercase"}}>
                    {isParent ? "Parent Portal" : orgTagline}
                  </span>
                </div>
              </div>
            </div>

            <p style={{fontSize:16,fontWeight:500,color:T.textSub,lineHeight:1.4,margin:"0 0 40px",maxWidth:480 }}>
              {isParent ? "Real-time performance analytics and coach feedback for your child." : orgTagline}
            </p>

            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {features.map(({icon:Icon,text,tag},i)=>(
                <div key={i} className="lp_feat" style={{
                  display:"flex",alignItems:"center",gap:16,padding:"16px",borderRadius:12,
                  background: isDark ? "rgba(255,255,255,0.03)" : "rgba(3,78,123,0.04)",
                  border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(3,78,123,0.08)",
                  animation:`lp_fadeUp 0.4s ease ${0.5+i*0.1}s both`,
                }}>
                  <div style={{width:40,height:40,borderRadius:10,background: isDark ? "rgba(247,206,11,0.08)" : "rgba(3,78,123,0.05)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <Icon size={18} style={{color:isDark ? T.yellow : T.blue}}/>
                  </div>
                  <span style={{flex:1,fontSize:15,fontWeight:600,color:T.textPrimary}}>{text}</span>
                  <span style={{fontFamily:D,fontSize:11,fontWeight:700,color:isDark ? T.yellow : T.blue, opacity:0.6}}>{tag}</span>
                </div>
              ))}
            </div>

            <div style={{marginTop:40,display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:14,color:T.textMuted}}>{isParent?"Staff member?":"Registered parent?"}</span>
              <button onClick={()=>switchPortal(isParent?"staff":"parent")} style={{background:"none",border:"none",padding:0,cursor:"pointer",fontSize:14,fontWeight:800,color:T.yellow,textTransform:"uppercase",letterSpacing:"0.05em"}}>
                {isParent?"Staff login →":"Parent login →"}
              </button>
            </div>
          </div>

          {/* RIGHT: Login Card */}
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",paddingRight:64}}>
            <div style={{width:"100%",maxWidth:480,position:"relative"}}>
              
              <div style={{ ...gc, padding:"48px 40px", animation:"lp_cardGlow 6s ease-in-out infinite", position:"relative" }}>
                <Corners color={T.yellow} size={28}/>

                {/* Tabs */}
                <div style={{display:"flex",gap:5,marginBottom:32,padding:5,background: isDark ? "rgba(0,0,0,0.3)" : "rgba(3,78,123,0.05)", borderRadius:12, border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(3,78,123,0.1)"}}>
                  {[{id:"staff",label:"Staff Login",Icon:FaUserShield},{id:"parent",label:"Parent Login",Icon:FaChild}].map(p=>{
                    const on=portal===p.id;
                    return(
                      <button key={p.id} className="lp_tab" onClick={()=>switchPortal(p.id)} style={{
                        flex:1,padding:"12px 8px",borderRadius:9,border:"none",cursor:"pointer",
                        display:"flex",alignItems:"center",justifyContent:"center",gap:9,
                        fontFamily:D,fontSize:15,fontWeight:700,letterSpacing:"0.06em",
                        background:on?`linear-gradient(135deg,${T.yellow},#d9b60a)`:"transparent",
                        color:on?T.blueDark:T.textMuted,
                        boxShadow:on?`0 4px 20px rgba(247,206,11,0.3)`:"none",
                      }}><p.Icon size={16}/>{p.label}</button>
                    );
                  })}
                </div>

                {/* Heading */}
                <div style={{marginBottom:32}}>
                  <h2 style={{fontFamily:D,fontSize:38,fontWeight:900,color:T.textPrimary,margin:0,letterSpacing:"0.04em"}}>
                    {isParent?"PARENT LOGIN":"SECURE SIGN IN"}
                  </h2>
                  <p style={{fontSize:15,color:T.textSub,marginTop:8, fontWeight:500}}>
                    Enter your credentials to access the {isParent?"Parent Intelligence Dashboard":"Management Suite"}.
                  </p>
                </div>

                {error&&(
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 18px",borderRadius:10,marginBottom:24,background:T.redBg,border:`1px solid ${T.redBdr}`,color:T.red,fontSize:14,fontWeight:700}}>
                    <FaExclamationCircle size={16}/>{error}
                  </div>
                )}

                {isParent&&(
                  <div style={{marginBottom:24}}>
                    <div ref={googleBtnRef} style={{width:"100%",borderRadius:10,overflow:"hidden"}}/>
                    <div style={{display:"flex",alignItems:"center",gap:12,margin:"24px 0"}}>
                      <div style={{flex:1,height:1,background:T.divider}}/>
                      <span style={{fontSize:12,fontWeight:800,color:T.textMuted,letterSpacing:"0.1em"}}>OR USE EMAIL</span>
                      <div style={{flex:1,height:1,background:T.divider}}/>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:20}}>
                  <div>
                    <label style={{display:"block",marginBottom:10,fontFamily:D,fontSize:13,fontWeight:700,letterSpacing:"0.12em",color:T.textLabel}}>EMAIL ADDRESS</label>
                    <div className="lp_wrap" style={{display:"flex",alignItems:"center",gap:14,background:T.inputBg,border:`1.5px solid ${T.inputBdr}`,borderRadius:12,padding:"16px 20px"}}>
                      <FaEnvelope size={18} style={{color:isDark ? "rgba(255,255,255,0.4)" : "rgba(3,78,123,0.3)"}}/>
                      <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email"
                        style={{border:"none",outline:"none",background:"transparent",color:T.inputText,fontSize:16,width:"100%",fontWeight:500}}/>
                    </div>
                  </div>
                  <div>
                    <label style={{display:"block",marginBottom:10,fontFamily:D,fontSize:13,fontWeight:700,letterSpacing:"0.12em",color:T.textLabel}}>PASSWORD</label>
                    <div className="lp_wrap" style={{display:"flex",alignItems:"center",gap:14,background:T.inputBg,border:`1.5px solid ${T.inputBdr}`,borderRadius:12,padding:"16px 20px"}}>
                      <FaLock size={18} style={{color:isDark ? "rgba(255,255,255,0.4)" : "rgba(3,78,123,0.3)"}}/>
                      <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••"
                        style={{border:"none",outline:"none",background:"transparent",color:T.inputText,fontSize:16,width:"100%",fontWeight:500}}/>
                    </div>
                  </div>
                  
                  <button type="submit" className="lp_btn" disabled={loading} style={{
                    marginTop:12,padding:"18px",borderRadius:12,border:"none",
                    fontFamily:D,fontSize:18,fontWeight:900,letterSpacing:"0.1em",
                    cursor:loading?"not-allowed":"pointer",
                    background:loading?"rgba(247,206,11,0.5)":`linear-gradient(135deg,${T.yellow},#d9b60a)`,
                    color:T.blueDark,boxShadow:`0 8px 30px rgba(247,206,11,0.3)`,display:"flex",alignItems:"center",justifyContent:"center",gap:12
                  }}>
                    {loading ? "AUTHENTICATING..." : <>{isParent?<FaChild size={20}/>:<FaSignInAlt size={20}/>} SECURE LOGIN</>}
                  </button>
                </form>

                <div style={{marginTop:32,textAlign:"center", color:T.textMuted, fontSize:13, fontWeight:500}}>
                   Trouble signing in? Contact academy support.
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Status */}
        <div style={{
          position:"absolute",bottom:0,left:0,right:0,zIndex:8,height:40,
          display:"flex",alignItems:"center",padding:"0 40px",gap:20,
          background: isDark ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.8)",
          borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(3,78,123,0.1)",
          backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)"
        }}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:"#4ade80",boxShadow:"0 0 10px rgba(74,222,128,0.5)"}}/>
            <span style={{fontFamily:D,fontSize:11,color:T.textMuted,letterSpacing:"0.12em"}}>SYSTEM SECURITY COMPLIANT</span>
          </div>
          <div style={{flex:1}}/>
          <span style={{fontFamily:D,fontSize:11,color:T.textLabel,letterSpacing:"0.08em"}}>
            {orgName.toUpperCase()} · v2.5 · SECURED END-TO-END
          </span>
        </div>
      </div>
    </>
  );
}
